import { NotificationsOutlined } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { Task } from "fhir/r5";

import { currentFeatures } from "@/configs/Settings";

import { NotificationsPanelBody } from "./NotificationsPanelBody";

interface DesktopNotificationsPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  unreadCount: number;
  loading: boolean;
  accessType?: string | null;
  tasks: Task[];
  updatingById: Record<string, boolean>;
  onToggleReadStatus: (task: Task) => void;
  onAccessMedicalRecord: (task: Task) => void;
  onRespondQuestionnaire: (task: Task) => void;
}

export function DesktopNotificationsPanel({
  open,
  anchorEl,
  onClose,
  unreadCount,
  loading,
  accessType,
  tasks,
  updatingById,
  onToggleReadStatus,
  onAccessMedicalRecord,
  onRespondQuestionnaire,
}: DesktopNotificationsPanelProps) {
  const theme = useTheme();
  const headerBg = currentFeatures?.defaultApp ? "primary800.main" : "#00A884";
  const subtitle =
    unreadCount > 0
      ? unreadCount === 1
        ? "1 não lida · últimos 30 dias"
        : `${unreadCount} não lidas · últimos 30 dias`
      : "Últimos 30 dias";

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      transition
      style={{ zIndex: theme.zIndex.modal + 1 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={180}>
          <Box>
            <ClickAwayListener onClickAway={onClose}>
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  width: 400,
                  maxWidth: "calc(100vw - 24px)",
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 24,
                  borderBottomRightRadius: 24,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 16px 40px rgba(0, 0, 0, 0.12)",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: headerBg,
                    color: "neutral0.main",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.common.white, 0.16),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <NotificationsOutlined
                      sx={{ fontSize: 18, color: "common.white" }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      noWrap
                      sx={{ color: "inherit" }}
                    >
                      Notificações
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: alpha(theme.palette.common.white, 0.78) }}
                    >
                      {subtitle}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    maxHeight: 420,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <NotificationsPanelBody
                    unreadCount={unreadCount}
                    loading={loading}
                    accessType={accessType}
                    tasks={tasks}
                    updatingById={updatingById}
                    closeHint="Clique fora para fechar"
                    onToggleReadStatus={onToggleReadStatus}
                    onAccessMedicalRecord={onAccessMedicalRecord}
                    onRespondQuestionnaire={onRespondQuestionnaire}
                    // Convite de teleconsultoria: fecha o painel ao entrar no atendimento.
                    onRequestClose={onClose}
                  />
                </Box>
              </Paper>
            </ClickAwayListener>
          </Box>
        </Fade>
      )}
    </Popper>
  );
}
